import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import _ from "lodash";
import { ReactElement, useState } from "react";
import { Form } from "react-router-dom";
import * as Yup from "yup";

import {
  CreateOwnedPhoneNumberPoolPurchaseMutation,
  CreateOwnedPhoneNumberPoolPurchaseMutationVariables,
  GetOwnedPhoneNumberPoolDetailsQuery,
  OwnedPhoneNumberPoolPurchaseOutcome,
  TrackedJobStatus,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import Table from "~frontend/components/Table";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { CREATE_OWNED_PHONE_NUMBER_POOL_PURCHASE } from "~frontend/graphql/ownedPhoneNumberPool";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import splitCommaSeparatedList from "~frontend/util/splitCommaSeparatedList";
import useMutation from "~frontend/util/useMutation";

function CreatePurchaseDialog({
  open,
  onClose,
  poolId,
}: {
  open: boolean;
  onClose: () => void;
  poolId: string;
}): ReactElement {
  const [createOwnedPhoneNumberPoolPurchase] = useMutation<
    CreateOwnedPhoneNumberPoolPurchaseMutation,
    CreateOwnedPhoneNumberPoolPurchaseMutationVariables
  >(CREATE_OWNED_PHONE_NUMBER_POOL_PURCHASE);

  const getErrorMessage = useErrorMessageFormatter();

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>Purchase Numbers</DialogTitle>
      <Formik
        initialValues={{
          [submitErrorField]: null,
          areaCodes: "",
          quantity: "",
        }}
        validationSchema={Yup.object().shape({
          areaCodes: Yup.string()
            .required()
            .test(
              "areaCodes",
              "Must be a comma-separated list of area codes",
              value => {
                if (!value) {
                  return true;
                }

                const areaCodes = splitCommaSeparatedList(value);
                return areaCodes.every(areaCode => /^\d{3}$/.test(areaCode));
              }
            ),
          quantity: Yup.number().required().min(1).max(2500),
        })}
        onSubmit={async (values, formik) => {
          const { errors } = await createOwnedPhoneNumberPoolPurchase({
            variables: {
              input: {
                ownedPhoneNumberPoolId: poolId,
                areaCodes: splitCommaSeparatedList(values.areaCodes),
                qty: Number(values.quantity),
              },
            },
          });

          if (!errors) {
            formik.resetForm();
            onClose();
          } else {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(errors),
            });
          }
        }}
      >
        {({ isSubmitting, handleSubmit }) => (
          <Form>
            <DialogContent>
              <FormikErrorBanner />
              <FormikTextField
                name="areaCodes"
                label="Area Codes (comma-separated)"
                fullWidth
              />
              <FormikTextField name="quantity" label="Quantity" fullWidth />
            </DialogContent>

            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="secondary"
              >
                Cancel
              </Button>

              <FormikSubmitButton
                onClick={() => handleSubmit()}
                color="primary"
                variant="contained"
                submitText="Purchase"
                loadingText="Purchasing..."
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

export default function PhoneNumberPoolPurchaseControls({
  pool,
}: {
  pool: GetOwnedPhoneNumberPoolDetailsQuery["getOwnedPhoneNumberPool"];
}): ReactElement | null {
  const isStaff = useCurrentUserIsStaff();

  const [createPurchaseDialogOpen, setCreatePurchaseDialogOpen] =
    useState(false);

  if (!isStaff) {
    return null;
  }

  return (
    <>
      <FlexBox flexDirection="row" justifyContent="space-between">
        <Typography variant="h2Bold">Purchases</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setCreatePurchaseDialogOpen(true)}
        >
          + New Purchase
        </Button>
      </FlexBox>
      <Table
        columns={[
          {
            key: "createdAt",
            title: "Created",
            renderValue: row => (
              <>
                {new Date(row.createdAt).toLocaleString()}
                {row.createdByUser && (
                  <>
                    <br />
                    <Typography variant="body2" color="textSecondary">
                      by {row.createdByUser.fullName}
                    </Typography>
                  </>
                )}
              </>
            ),
          },
          {
            key: "status",
            title: "Status",
            renderValue: row => {
              if (!row.trackedJob) {
                return "In Progress";
              }

              switch (row.trackedJob.status) {
                case TrackedJobStatus.Created:
                case TrackedJobStatus.Queued:
                case TrackedJobStatus.Running:
                  return "In Progress";

                case TrackedJobStatus.Failed:
                case TrackedJobStatus.Stalled:
                case TrackedJobStatus.Canceled:
                case TrackedJobStatus.Aborted:
                  return "Failed";
                case TrackedJobStatus.Done: {
                  switch (row.trackedJob.result!.outcome) {
                    case OwnedPhoneNumberPoolPurchaseOutcome.CompleteNone:
                      return "No Numbers Available";
                    case OwnedPhoneNumberPoolPurchaseOutcome.CompletePartial:
                      return "Partial Success";
                    case OwnedPhoneNumberPoolPurchaseOutcome.CompleteFull:
                      return "Success";
                  }
                }
              }

              // Exhaustive check
              row.trackedJob.status satisfies never;
            },
          },
          {
            key: "areaCodes",
            title: "Area Codes",
            renderValue: row => row.areaCodesRequested.join(", "),
          },
          {
            key: "requestedCount",
            title: "Requested",
            renderValue: row => row.qtyRequested.toLocaleString(),
          },
          {
            key: "purchasedCount",
            title: "Purchased",
            renderValue: row => row.qtyPurchased?.toLocaleString() ?? "",
          },
          {
            key: "messages",
            title: "Notes",
            renderValue: row => {
              switch (row.trackedJob?.status) {
                case TrackedJobStatus.Failed:
                case TrackedJobStatus.Stalled:
                  return (
                    <Box sx={{ textAlign: "left" }}>
                      {row.trackedJob.errorMessage}
                    </Box>
                  );
                case TrackedJobStatus.Done:
                  return (
                    <List>
                      {row.trackedJob.result?.notes.map(n => (
                        <ListItem sx={{ pl: 0 }}>{n}</ListItem>
                      ))}
                    </List>
                  );
              }
            },
          },
        ]}
        rows={_.sortBy(pool.purchases, p => -p.createdAt)}
        emptyState="No phone number purchases"
        noFooter={true}
      />
      <CreatePurchaseDialog
        open={createPurchaseDialogOpen}
        onClose={() => setCreatePurchaseDialogOpen(false)}
        poolId={pool.id}
      />
    </>
  );
}
